import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase/config';
import { doc, getDoc, collection, query, where, getDocs, limit as firestoreLimit, updateDoc, setDoc } from 'firebase/firestore';

interface SubscriptionLimits {
  maxItems: number;
  maxMembers: number;
  maxBarcodeScans: number;
  maxCustomCategories: number;
  hasAdvancedAnalytics: boolean;
  hasMealPlanning: boolean;
  hasNutritionGoals: boolean;
  hasAdvancedShoppingList: boolean;
}

interface SubscriptionStatus {
  isActive: boolean;
  isTrial: boolean;
  trialEndsAt: Date | null;
  limits: SubscriptionLimits;
  loading: boolean;
}

const FREE_LIMITS: SubscriptionLimits = {
  maxItems: 15,  // âœ… CORRECT: 15 items for free users
  maxMembers: 2,
  maxBarcodeScans: 5,  // âœ… 5 scans per month for free users
  maxCustomCategories: 3,  // âœ… 3 custom categories for free users
  hasAdvancedAnalytics: false,
  hasMealPlanning: false,  // âœ… Basic view only
  hasNutritionGoals: false,  // âœ… View inventory nutrition only
  hasAdvancedShoppingList: false,  // âœ… Basic list only
};

const PREMIUM_LIMITS: SubscriptionLimits = {
  maxItems: Infinity,
  maxMembers: Infinity,
  maxBarcodeScans: Infinity,  // âœ… ADDED: Unlimited for premium
  maxCustomCategories: Infinity,  // âœ… Unlimited custom categories
  hasAdvancedAnalytics: true,
  hasMealPlanning: true,  // âœ… Full meal planning calendar
  hasNutritionGoals: true,  // âœ… Full goal tracking with charts
  hasAdvancedShoppingList: true,  // âœ… Auto-generate + patterns + share
};

export function useSubscription(): SubscriptionStatus {
  const { user } = useAuth();
  const [status, setStatus] = useState<SubscriptionStatus>({
    isActive: false,
    isTrial: false,
    trialEndsAt: null,
    limits: FREE_LIMITS,
    loading: true,
  });

  useEffect(() => {
    if (!user) {
      setStatus({
        isActive: false,
        isTrial: false,
        trialEndsAt: null,
        limits: FREE_LIMITS,
        loading: false,
      });
      return;
    }

    async function checkSubscription() {
      try {
        // Get user's household
        const householdsQuery = query(
          collection(db, 'households'),
          where('members', 'array-contains', user!.uid),
          firestoreLimit(1)
        );

        const householdsSnapshot = await getDocs(householdsQuery);

        if (householdsSnapshot.empty) {
          setStatus({
            isActive: false,
            isTrial: false,
            trialEndsAt: null,
            limits: FREE_LIMITS,
            loading: false,
          });
          return;
        }

        const householdDoc = householdsSnapshot.docs[0];
        const householdData = householdDoc.data();

        // Check subscription status
        const subscriptionStatus = householdData.subscriptionStatus || 'inactive';
        const trialEndsAt = householdData.trialEndsAt 
          ? new Date(householdData.trialEndsAt)
          : null;

        const now = new Date();
        const isTrialActive = trialEndsAt && trialEndsAt > now;
        const isSubscriptionActive = subscriptionStatus === 'active';

        setStatus({
          isActive: isSubscriptionActive || !!isTrialActive,
          isTrial: !!isTrialActive && !isSubscriptionActive,
          trialEndsAt,
          limits: isSubscriptionActive ? PREMIUM_LIMITS : FREE_LIMITS,  // Only paying customers get unlimited
          loading: false,
        });
      } catch (error) {
        console.error('Error checking subscription:', error);
        setStatus({
          isActive: false,
          isTrial: false,
          trialEndsAt: null,
          limits: FREE_LIMITS,
          loading: false,
        });
      }
    }

    checkSubscription();
  }, [user]);

  return status;
}

// Hook to check if user can add more items
export function useSubscriptionLimits() {
  const { user } = useAuth();
  const subscription = useSubscription();
  const [currentItemCount, setCurrentItemCount] = useState(0);
  const [currentMemberCount, setCurrentMemberCount] = useState(0);
  const [currentScanCount, setCurrentScanCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [householdId, setHouseholdId] = useState<string>('');

  useEffect(() => {
    if (!user || subscription.loading) {
      return;
    }

    async function fetchCounts() {
      try {
        // Get user's household
        const householdsQuery = query(
          collection(db, 'households'),
          where('members', 'array-contains', user!.uid),
          firestoreLimit(1)
        );

        const householdsSnapshot = await getDocs(householdsQuery);

        if (householdsSnapshot.empty) {
          setLoading(false);
          return;
        }

        const hId = householdsSnapshot.docs[0].id;
        const householdData = householdsSnapshot.docs[0].data();
        setHouseholdId(hId);

        // Count active items
        const itemsQuery = query(
          collection(db, 'items'),
          where('householdId', '==', hId),
          where('status', '==', 'active')
        );

        const itemsSnapshot = await getDocs(itemsQuery);
        setCurrentItemCount(itemsSnapshot.size);

        // Count members
        const members = householdData.members || [];
        setCurrentMemberCount(members.length);

        // Get barcode scan count for current month
        const currentMonth = new Date().toISOString().slice(0, 7); // YYYY-MM format
        const barcodeScans = householdData.barcodeScans || {};
        
        // Check if it's a new month - reset count if needed
        if (barcodeScans.currentMonth === currentMonth) {
          setCurrentScanCount(barcodeScans.scanCount || 0);
        } else {
          // New month - reset count
          setCurrentScanCount(0);
          
          // Update Firestore to reset for new month (only skip for paying premium)
          if (subscription.limits.maxBarcodeScans !== Infinity) {
            await updateDoc(doc(db, 'households', hId), {
              'barcodeScans.currentMonth': currentMonth,
              'barcodeScans.scanCount': 0,
              'barcodeScans.lastReset': new Date()
            });
          }
        }

        setLoading(false);
      } catch (error) {
        console.error('Error fetching counts:', error);
        setLoading(false);
      }
    }

    fetchCounts();
  }, [user, subscription.loading, subscription.isActive]);

  // Function to increment barcode scan count
  async function incrementScanCount() {
    if (!householdId || subscription.limits.maxBarcodeScans === Infinity) {
      // Premium users (unlimited) don't need tracking
      return;
    }

    try {
      const currentMonth = new Date().toISOString().slice(0, 7);
      const householdRef = doc(db, 'households', householdId);
      
      await updateDoc(householdRef, {
        'barcodeScans.currentMonth': currentMonth,
        'barcodeScans.scanCount': currentScanCount + 1,
        'barcodeScans.lastScan': new Date()
      });

      setCurrentScanCount(currentScanCount + 1);
    } catch (error) {
      console.error('Error incrementing scan count:', error);
    }
  }

  return {
    canAddItem: currentItemCount < subscription.limits.maxItems,
    canAddMember: currentMemberCount < subscription.limits.maxMembers,
    canScanBarcode: currentScanCount < subscription.limits.maxBarcodeScans,
    currentItemCount,
    currentMemberCount,
    currentScanCount,
    maxItems: subscription.limits.maxItems,
    maxMembers: subscription.limits.maxMembers,
    maxBarcodeScans: subscription.limits.maxBarcodeScans,
    maxCustomCategories: subscription.limits.maxCustomCategories,
    hasAdvancedAnalytics: subscription.limits.hasAdvancedAnalytics,
    hasMealPlanning: subscription.limits.hasMealPlanning,
    hasNutritionGoals: subscription.limits.hasNutritionGoals,
    hasAdvancedShoppingList: subscription.limits.hasAdvancedShoppingList,
    isActive: subscription.isActive,
    isTrial: subscription.isTrial,
    trialEndsAt: subscription.trialEndsAt,
    loading: loading || subscription.loading,
    incrementScanCount,  // âœ… Export this function
  };
}
